import { compose, mdsWidget } from 'dojo/_base/declare';
import template from './ApproverInput.html?raw';

import _StatefulSection from '../../../moreCore/UI/_StatefulSection/_StatefulSection';
import lang from 'dojo/_base/lang';
import SearchSelect from '../../../moreCore/UI/SearchSelect/SearchSelect';
import _GlobalSelectEntityDialogsMixin from '../../_loadingHelpers/_GlobalSelectEntityDialogsMixin';
import HintBox from '../../../moreCore/UI/HintBox/HintBox';
import { SelectEntityDialogTypes } from "../../../moreCore/_loadingHelpers/SelectEntityDialog";
import { SignatureAddress, SignatureLanguage } from "../DigitalSignatureTypes";
import CheckBox from '../../../moreCore/UI/CheckBox/CheckBox';
import Edit from '../../../moreCore/UI/Edit/Edit';

class ApproverInput extends compose(_StatefulSection, _GlobalSelectEntityDialogsMixin) {
    templateString: any = template;

    private inputID: number;
    private approverKey: string;
    private emailKey: string;
    private languageKey: string;
    private idKey: string;
    private statusKey: string;
    private urlKey: string;
    private mailSendKey: string;

    private isManualKey: string;
    private salutationKey: string;
    private firstnameKey: string;
    private lastnameKey: string;

    private heading: HTMLElement;

    private inputApproverLanguageCode: MdsLax<InstanceType<typeof SearchSelect>>;
    private inputApprover: MdsLax<InstanceType<typeof HintBox>>;
    private sendLinkBtn: HTMLButtonElement;

    private inputIsManual: MdsLax<InstanceType<typeof CheckBox>>;
    private inputSalutation: MdsLax<InstanceType<typeof SearchSelect>>;
    private inputFirstname: MdsLax<InstanceType<typeof Edit>>;
    private inputLastname: MdsLax<InstanceType<typeof Edit>>;
    private inputEmail: MdsLax<InstanceType<typeof Edit>>;

    private containerAddressMode: HTMLDivElement;

    constructor(inputID: number) {
        super(...(arguments as unknown as []));
        this.inputID = inputID;

        switch (inputID) {
            case 1:
                this.approverKey = "ref_approver";
                this.emailKey = "mail_address_approver";
                this.languageKey = "ref_language_approver_1";
                this.idKey = "approver_id";
                this.statusKey = "ref_status_signature_entity_approver_1.code";
                this.urlKey = "approver_entity_url";
                this.mailSendKey = "is_mail_send_approver_1";
                this.isManualKey = "is_manual_approver";
                this.salutationKey = "approver_salutation";
                this.firstnameKey = "approver_name_first";
                this.lastnameKey = "approver_name";
                break;
            case 2:
                this.approverKey = `ref_approver_${inputID}`;
                this.emailKey = `mail_address_approver_${inputID}`;
                this.languageKey = `ref_language_approver_${inputID}`;
                this.idKey = `approver_id_${inputID}`;
                this.statusKey = `ref_status_signature_entity_approver_${inputID}.code`;
                this.urlKey = `approver_entity_url_${inputID}`;
                this.mailSendKey = `is_mail_send_approver_${inputID}`;
                this.isManualKey = `is_manual_approver_${inputID}`;
                this.salutationKey = `approver_salutation_${inputID}`;
                this.firstnameKey = `approver_name_first_${inputID}`;
                this.lastnameKey = `approver_name_${inputID}`;
                break;
            default:
                console.error("Error: Invalid input ID");
                return;
        }
    }

    private _onClickSendLink(evt: MouseEvent): void {
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_SendMail", { type: "approver", inputID: this.inputID, entity: this.getAttributeValue("___root") }, lang.hitch(this, this._onSendMailResponse), lang.hitch(this, this._onSendMailError));
    }

    private _onSendMailResponse(response): void {
        console.log(response);
    }

    private _onSendMailError(error): void {
        console.error(error);
        this.showToast(error.Message, "fas fa-exclamation-triangle");
    }

    protected _onChanged(attribute: string, value: any): void {
        switch (attribute) {
            case this.approverKey:
                this.setApproverValues(value);
                break;
            case this.isManualKey:
                this._toggleManualMode(value, false);
                break;
        }

        let changedData = this.getChangedData();
        this.emit("changed", changedData);
    }

    private setApproverValues(value: any) {
        switch (value?.Classname) {
            case "persons":
                this.setAttributeValue(this.emailKey, value?.ref_vw_telecom_addresses?.emailaddress);
                this.setAttributeValue(this.salutationKey, value?.ref_salutation);
                this.setAttributeValue(this.firstnameKey, value?.name_first);
                this.setAttributeValue(this.lastnameKey, value?.name);
                break;
            case "associates":
            case "contact_persons":
                this.setAttributeValue(this.emailKey, value?.ref_vw_telecom_addresses?.emailaddress);
                this.setAttributeValue(this.salutationKey, value?.ref_person?.ref_salutation);
                this.setAttributeValue(this.firstnameKey, value?.ref_person?.name_first);
                this.setAttributeValue(this.lastnameKey, value?.ref_person?.name);
                break;
            default:
                if (this.getAttributeValue(this.isManualKey)) {
                    this.setAttributeValue(this.emailKey, this.getAttributeValue(this.emailKey));
                    this.setAttributeValue(this.salutationKey, this.getAttributeValue(this.salutationKey));
                    this.setAttributeValue(this.firstnameKey, this.getAttributeValue(this.firstnameKey));
                    this.setAttributeValue(this.lastnameKey, this.getAttributeValue(this.lastnameKey));
                } else {
                    this.setAttributeValue(this.emailKey, null);
                    this.setAttributeValue(this.salutationKey, null);
                    this.setAttributeValue(this.firstnameKey, null);
                    this.setAttributeValue(this.lastnameKey, null);
                }
                break;
        }
    }

    private _toggleManualMode(isManual: boolean, isAfterLoad: boolean = true): void {
        if (isManual) {
            this.hideNode(this.containerAddressMode);
            if (!isAfterLoad) {
                this.setAttributeValue(this.approverKey, null);
                this.setAttributeValue(this.salutationKey, null);
                this.setAttributeValue(this.firstnameKey, null);
                this.setAttributeValue(this.lastnameKey, null);
                this.setAttributeValue(this.emailKey, null);
            }
        } else {
            this.hideNode(this.containerAddressMode, false);
            if (!isAfterLoad) {
                let approver = this.getAttributeValue(this.approverKey);
                this.setApproverValues(approver);
            }
        }
        this.inputSalutation.setReadonly(!isManual);
        this.inputFirstname.setReadonly(!isManual);
        this.inputLastname.setReadonly(!isManual);
        this.inputEmail.setReadonly(!isManual);
    }

    private _loadLanguageValues(currentValue: SignatureLanguage): void {
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_GetLanguageCodes", {}, lang.hitch(this, this._onLoadLanguageValuesResponse, currentValue), lang.hitch(this, this._onLoadLanguageValuesError));
    }

    private _onLoadLanguageValuesResponse(currentValue: SignatureLanguage, response: Array<SignatureLanguage>): void {
        console.log(currentValue, response);
        this.inputApproverLanguageCode.setData(currentValue, response, true);
    }

    private _onLoadLanguageValuesError(error): void {
        console.error(error);
    }

    private _loadSalutationValues(currentValue: any): void {
        this.RequestManager.callfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_GetSalutations", {}, lang.hitch(this, this._onLoadSalutationValuesResponse, currentValue), lang.hitch(this, this._onLoadSalutationValuesError));
    }

    private _onLoadSalutationValuesResponse(currentValue: any, response: Array<any>): void {
        this.inputSalutation.setData(currentValue, response, true);
    }

    private _onLoadSalutationValuesError(error): void {
        console.error(error);
    }

    protected afterSetData(): void {
        super.afterSetData.apply(this, arguments as any);

        let isManual = this.getAttributeValue(this.isManualKey);
        this._toggleManualMode(!!isManual);

        let language = this.getAttributeValue(this.languageKey);
        console.log(language);
        //this._loadLanguageValues(language);

        let salutation = this.getAttributeValue(this.salutationKey);
        this._loadSalutationValues(salutation);

        let link = this.getAttributeValue(this.urlKey);
        let hasLink = link ? true : false;
        this.disableEmailButton(!hasLink);
    }

    protected onDirtyChange(newState: boolean): void {
        if (newState) this.disableEmailButton(true);
    }

    private disableEmailButton(disable: boolean): void {
        this.disableNode(this.sendLinkBtn, disable);
    }

    private _onClickSED(mode: string, evt: MouseEvent): void {
        this.showSelectEntityDialog(evt, mode, lang.hitch(this, this._onSEDCallback));
    }

    private _onSEDCallback(mode: string, data: Array<string>, items: Array<SelectEntityDialogTypes.Item<SignatureAddress>>): void {
        if (!items || items.length === 0) return;

        let entity;
        switch (mode) {
            case "selectApprover":
                entity = items[0].entry.data;
                this.setAttributeValue(this.approverKey, entity);
                break;
        }
    }

    setReadonly(readonly?: boolean | "initial"): void {
        super.setReadonly.apply(this, arguments as any);

        this.disableEmailButton(true);
    }

    postCreate(): void {
        super.postCreate.apply(this, arguments as any);

        this.heading.innerHTML = this.translate(`DigitalSignature_Label_Approver${this.inputID}`);

        this.SEDconfigCallfunction = "Webtool_API_DigitalSignature__DigitalSignatureEditor_GetSEDConfig";
        this.inputApprover.addInlineButton(lang.hitch(this, this._onClickSED, "selectApprover"));

        this.inputApproverLanguageCode.setCallfunction("Webtool_API_DigitalSignature__DigitalSignatureEditor_GetLanguageCodes");
    }

    startup(): void {
        super.startup.apply(this, arguments as any);
    }
}

export default mdsWidget(ApproverInput);
